/**
 * Gibbon store discounts (Shopify discounts ticked "Show on store" in Gibbon Admin).
 * Liquid renders every price (snippets/gibbon-store-sale.liquid). This script only exists for code rules and
 * end dates, and loads only on pages that contain such a price (layout/theme.liquid):
 *  - <gibbon-sd> wrappers: swaps in the <template> state that matches the shopper's cart codes, or the catalog
 *    state when a sale ends;
 *  - #priceDiv (PDP): feeds the same states into data-price / data-compare and lets the page's own
 *    refreshPrices() repaint ('sb:board-price-change'); never writes inside #priceDiv;
 *  - legacy theme.js variant pickers: repaints [data-product-price] after a variant change.
 * It reads /cart.js only when a code candidate exists, and writes only the _gb_codes cart attribute.
 * Port of the Horizon theme's assets/gb-discount-codes.js.
 */
(function () {
  'use strict';

  var WRAPPER = '[data-gibbon-sd]';
  var PDP_MAP = 'script[data-gibbon-sd-pdp]';
  var VARIANT_MAP = 'script[data-gibbon-sd-variants]';

  function normalizeCodes(list) {
    var seen = {};
    return list.map(function (code) { return String(code).trim().toUpperCase(); })
      .filter(function (code) {
        if (!code || seen[code]) return false;
        seen[code] = true;
        return true;
      })
      .sort();
  }

  function decodeCode(value) {
    var text = String(value || '').replace(/\+/g, ' ');
    try { text = decodeURIComponent(text); } catch (error) { /* keep the raw text */ }
    return text.trim().toUpperCase();
  }

  function live(state, now) {
    return !!state && !(state.ends > 0 && now >= state.ends);
  }

  function lowestLive(states, now) {
    var best = null;
    (states || []).forEach(function (state) {
      if (live(state, now) && (!best || state.sale < best.sale)) best = state;
    });
    return best;
  }

  /**
   * Horizon rules: until the cart's codes are known only expiry applies, and an ended sale falls back to the next live
   * automatic sale (entry.autos, the fallback chain); once codes are known, the lowest of the live automatic sales and
   * every known code wins. null means the catalog price.
   */
  function choose(entry, codes, now) {
    var names = Object.keys(entry.codes);
    if (codes && names.length) {
      var best = lowestLive(entry.autos, now);
      names.forEach(function (name) {
        var state = entry.codes[name];
        if (codes.has(name) && live(state, now) && (!best || state.sale < best.sale)) best = state;
      });
      return best;
    }
    if (live(entry.show, now)) return entry.show;
    return entry.show ? lowestLive(entry.autos, now) : null;
  }

  function fromArray(values) {
    return values ? { sale: Number(values[0]), compare: Number(values[1]), ends: Number(values[2]) || 0 } : null;
  }

  /**
   * {"swap","base":[price,compare],"show":[sale,compare,ends]|null,"auto":[…]|null,"autos"?:[[…],…],"codes":{CODE:[…]}}
   * "auto" is the best automatic sale; "autos" (only when there is more than one) is the whole fallback chain.
   */
  function mapEntry(raw) {
    if (!raw || !raw.base) return null;
    var autos = Array.isArray(raw.autos) ? raw.autos.map(fromArray) : raw.auto ? [fromArray(raw.auto)] : [];
    var entry = { base: { price: Number(raw.base[0]), compare: Number(raw.base[1]) }, show: fromArray(raw.show),
      autos: autos, codes: {} };
    // JSON keys are already decoded by Liquid (url_decode); only normalise them.
    Object.keys(raw.codes || {}).forEach(function (code) { entry.codes[String(code).trim().toUpperCase()] = fromArray(raw.codes[code]); });
    return entry;
  }

  function values(entry, codes, now) {
    var state = choose(entry, codes, now);
    return { state: state, price: state ? state.sale : entry.base.price, compare: state ? state.compare : entry.base.compare };
  }

  function createController(config, deps) {
    deps = deps || {};
    var doc = deps.doc || document;
    var win = deps.win || window;
    var clock = deps.now || function () { return Date.now(); };
    var later = deps.setTimeout || win.setTimeout.bind(win);
    var cancel = deps.clearTimeout || win.clearTimeout.bind(win);
    var fetcher = deps.fetcher || win.fetch.bind(win);
    var origin = deps.origin || win.location.origin;
    var shopifyRoot = win.Shopify && win.Shopify.routes && win.Shopify.routes.root;
    var root = deps.root || shopifyRoot || config.root || '/';
    var base = new URL(root.charAt(root.length - 1) === '/' ? root : root + '/', origin);
    if (base.origin !== origin) throw new Error('Discount routes must be same-origin');

    var codes;
    var attribute = config.codes || '';
    var running = false;
    var pending = false;
    var refreshPending = false;
    var timer;
    var expiryTimer;
    var logged = false;
    var shown = new WeakMap();
    var painted = new WeakMap();
    var maps = new WeakMap();

    function all(selector) { return Array.prototype.slice.call(doc.querySelectorAll(selector)); }
    function templates(node) {
      return Array.prototype.slice.call(node.children).filter(function (child) { return child.nodeName === 'TEMPLATE'; });
    }
    function parse(script) {
      if (!maps.has(script)) {
        var data = null;
        try { data = JSON.parse(script.textContent); } catch (error) { data = null; }
        maps.set(script, data);
      }
      return maps.get(script);
    }

    // <gibbon-sd> wrappers --------------------------------------------------------------------------------------
    function wrapperEntry(node) {
      var entry = { show: null, autos: [], codes: {}, base: null };
      if (node.hasAttribute('data-sd-sale')) {
        entry.show = { sale: Number(node.getAttribute('data-sd-sale')), ends: Number(node.getAttribute('data-sd-ends')) || 0 };
      }
      templates(node).forEach(function (template) {
        var kind = template.getAttribute('data-sd');
        var state = { sale: Number(template.getAttribute('data-sd-sale')), ends: Number(template.getAttribute('data-sd-ends')) || 0,
          template: template };
        if (kind === 'base') entry.base = template;
        else if (kind === 'auto') entry.autos.push(state);
        else if (kind === 'code') entry.codes[decodeCode(template.getAttribute('data-sd-code'))] = state;
      });
      return entry;
    }

    function swap(node, template) {
      var anchor = null;
      Array.prototype.slice.call(node.childNodes).forEach(function (child) {
        if (child.nodeName === 'TEMPLATE') anchor = anchor || child;
        else node.removeChild(child);
      });
      node.insertBefore(template.content.cloneNode(true), anchor);
    }

    function renderWrapper(node, now) {
      var entry = wrapperEntry(node);
      var state = choose(entry, codes, now);
      var key = state ? String(state.sale) : '';
      var current = shown.has(node) ? shown.get(node) : (entry.show ? String(entry.show.sale) : '');
      if (key !== current) {
        var template = state ? state.template : entry.base;
        if (!template) return state;
        swap(node, template);
        shown.set(node, key);
      }
      return state;
    }

    // #priceDiv (product-template-new, -dowel, -drew, -slackyard) -----------------------------------------------
    function pdpTargets() {
      return all(PDP_MAP).map(function (script) {
        var data = parse(script);
        var section = doc.getElementById('ProductSection-' + script.getAttribute('data-gibbon-sd-pdp'));
        var priceDiv = (section && section.querySelector('#priceDiv')) || doc.getElementById('priceDiv');
        var board = (section && section.getAttribute('data-sb-active-board')) || 'product';
        return { data: data, priceDiv: priceDiv, entry: data ? mapEntry(data[board]) : null };
      }).filter(function (target) { return target.priceDiv && target.entry; });
    }

    function renderPdp(target, now) {
      var next = values(target.entry, codes, now);
      var data = target.priceDiv.dataset;
      if (String(next.price) !== data.price || String(next.compare) !== data.compare) {
        data.price = String(next.price);
        data.compare = String(next.compare);
        doc.dispatchEvent(new CustomEvent('sb:board-price-change'));
      }
      return next.state;
    }

    // Legacy theme.js variant pickers (snippets/product-template.liquid) -----------------------------------------
    function formatMoney(cents) {
      var theme = win.theme;
      if (theme && theme.Currency && typeof theme.Currency.formatMoney === 'function') {
        return theme.Currency.formatMoney(cents, theme.settings && theme.settings.moneyFormat);
      }
      return (cents / 100).toFixed(2);
    }

    /** Same DOM writes as theme.Product.updatePrice in assets/theme.js. */
    function paintLegacy(container, price, compare) {
      var priceEl = container.querySelector('[data-product-price]');
      if (!priceEl) return;
      var compareEl = container.querySelector('[data-product-price-compare]');
      var wrapper = container.querySelector('[data-price-wrapper]');
      var compareA11y = container.querySelector('[data-compare-a11y]');
      var priceA11y = container.querySelector('[data-price-a11y]');
      priceEl.innerHTML = formatMoney(price);
      if (compare > price && compareEl && wrapper) {
        compareEl.innerHTML = formatMoney(compare);
        wrapper.classList.remove('hide');
        priceEl.classList.add('sale-price');
        if (compareA11y) compareA11y.setAttribute('aria-hidden', 'false');
        if (priceA11y) priceA11y.setAttribute('aria-hidden', 'false');
      } else {
        if (wrapper && compareA11y) {
          wrapper.classList.add('hide');
          compareA11y.setAttribute('aria-hidden', 'true');
        }
        priceEl.classList.remove('sale-price');
        if (priceA11y) priceA11y.setAttribute('aria-hidden', 'true');
      }
    }

    /** The map script carries the current variant (data-current, updated on variantChange) and its container. */
    function legacyTargets() {
      return all(VARIANT_MAP).map(function (script) {
        return { script: script, container: script.closest('[data-section-type]') || script.parentNode };
      });
    }

    function renderLegacy(target, now, variant) {
      var variantId = String(variant ? variant.id : target.script.getAttribute('data-current'));
      var entry = mapEntry((parse(target.script) || {})[variantId]);
      var next = entry ? values(entry, codes, now)
        : variant ? { state: null, price: variant.price, compare: variant.compare_at_price || 0 } : null;
      if (!next) return null;
      var key = variantId + '|' + next.price + '|' + next.compare;
      if (!variant && !painted.has(target.script)) {
        // Liquid already painted the first variant's show state.
        var first = entry.show ? entry.show.sale + '|' + entry.show.compare : entry.base.price + '|' + entry.base.compare;
        painted.set(target.script, variantId + '|' + first);
      }
      if (variant || painted.get(target.script) !== key) {
        paintLegacy(target.container, next.price, next.compare);
        painted.set(target.script, key);
      }
      return next.state;
    }

    // Rendering, cart codes and scheduling ---------------------------------------------------------------------
    function render() {
      var now = clock() / 1000;
      var nextEnd = Infinity;
      function track(state) {
        if (state && state.ends > 0 && now < state.ends) nextEnd = Math.min(nextEnd, state.ends);
      }
      // A swap can bring in nested wrappers (a card's "From" price has its own states), so render those too.
      var done = [];
      var nodes = all(WRAPPER);
      for (var pass = 0; nodes.length && pass < 4; pass += 1) {
        nodes.forEach(function (node) { done.push(node); track(renderWrapper(node, now)); });
        nodes = all(WRAPPER).filter(function (node) { return done.indexOf(node) === -1; });
      }
      pdpTargets().forEach(function (target) { track(renderPdp(target, now)); });
      legacyTargets().forEach(function (target) { track(renderLegacy(target, now)); });
      cancel(expiryTimer);
      expiryTimer = undefined;
      if (nextEnd < Infinity) {
        expiryTimer = later(render, Math.min(2147483647, Math.max(1, nextEnd * 1000 - clock())));
      }
    }

    function hasCodes(raw) {
      return !!raw && Object.keys(raw).some(function (key) { return raw[key] && Object.keys(raw[key].codes || {}).length > 0; });
    }

    function needsCart() {
      return all(WRAPPER).some(function (node) { return !!node.querySelector('template[data-sd="code"]'); }) ||
        all(PDP_MAP).some(function (script) { return hasCodes(parse(script)); }) ||
        all(VARIANT_MAP).some(function (script) { return hasCodes(parse(script)); });
    }

    function json(path, options) {
      return fetcher(new URL(path, base).href, options).then(function (response) {
        if (!response.ok) throw new Error('Cart codes request failed (' + response.status + ')');
        return response.json();
      });
    }

    function check(refresh) {
      if (running) {
        pending = true;
        refreshPending = refreshPending || !!refresh;
        return Promise.resolve();
      }
      running = true;
      // Expiry never needs the cart, so it runs first and survives request failures.
      try { render(); } catch (error) { report(error); }
      var work = Promise.resolve();
      if (needsCart() && (refresh || !codes)) {
        work = json('cart.js', { credentials: 'same-origin', cache: 'no-store' }).then(function (cart) {
          var next = normalizeCodes((cart.discount_codes || []).map(function (entry) { return entry && entry.code; })
            .filter(function (code) { return typeof code === 'string'; }));
          var stored = cart.attributes && cart.attributes._gb_codes != null ? cart.attributes._gb_codes : attribute;
          var write = next.join(',') !== normalizeCodes(String(stored).split(',')).join(',')
            ? json('cart/update.js', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ attributes: { _gb_codes: next.join(',') } }) })
            : null;
          return Promise.resolve(write).then(function () {
            attribute = next.join(',');
            codes = new Set(next);
            render();
          });
        });
      }
      return work.catch(report).then(function () {
        running = false;
        if (pending) {
          pending = false;
          var again = refreshPending;
          refreshPending = false;
          schedule(again);
        }
      });
    }

    function report(error) {
      if (!logged && win.console && win.console.debug) win.console.debug('[Gibbon discounts] Keeping Liquid prices.', error);
      logged = true;
    }

    function schedule(refresh) {
      refreshPending = refreshPending || !!refresh;
      cancel(timer);
      timer = later(function () {
        var again = refreshPending;
        refreshPending = false;
        check(again);
      }, 180);
    }

    var onCart = function () { schedule(true); };
    var onDom = function () { schedule(false); };
    var onPageShow = function (event) { if (event.persisted) schedule(true); };
    // setBoard() writes the board's Liquid state into #priceDiv, then dispatches this; re-apply in the same task.
    var onBoard = function () { try { render(); } catch (error) { report(error); } };
    // theme.js dispatches variantChange (not bubbling) before its own updatePrice(), so run after it.
    var onVariant = function (event) {
      var container = event.target;
      var variant = event.detail && event.detail.variant;
      var script = variant && container && container.querySelector ? container.querySelector(VARIANT_MAP) : null;
      if (!script) return;
      var run = function () {
        script.setAttribute('data-current', String(variant.id));
        renderLegacy({ script: script, container: container }, clock() / 1000, variant);
      };
      if (typeof win.queueMicrotask === 'function') win.queueMicrotask(run); else Promise.resolve().then(run);
    };
    var cartEvents = ['cart:updated', 'cart:build', 'ajaxProduct:added', 'liquid-ajax-cart:request-end', 'CartDrawer:open'];

    function start() {
      cartEvents.forEach(function (type) { doc.addEventListener(type, onCart); });
      doc.addEventListener('recommendations:loaded', onDom);
      doc.addEventListener('gibbon:board-switch', onBoard);
      doc.addEventListener('variantChange', onVariant, true);
      win.addEventListener('pageshow', onPageShow);
      var Observer = deps.MutationObserver || win.MutationObserver;
      var observer = Observer ? new Observer(function (records) {
        var added = records.some(function (record) {
          return Array.prototype.some.call(record.addedNodes, function (node) {
            return node.nodeType === 1 && (node.matches(WRAPPER) || node.matches(PDP_MAP) || node.matches(VARIANT_MAP) ||
              !!node.querySelector(WRAPPER + ',' + PDP_MAP + ',' + VARIANT_MAP));
          });
        });
        if (added) schedule(false);
      }) : null;
      if (observer) observer.observe(doc.body, { childList: true, subtree: true });
      check(false);
      return function stop() {
        if (observer) observer.disconnect();
        cancel(timer);
        cancel(expiryTimer);
        cartEvents.forEach(function (type) { doc.removeEventListener(type, onCart); });
        doc.removeEventListener('recommendations:loaded', onDom);
        doc.removeEventListener('gibbon:board-switch', onBoard);
        doc.removeEventListener('variantChange', onVariant, true);
        win.removeEventListener('pageshow', onPageShow);
      };
    }

    return { check: check, render: render, start: start, codes: function () { return codes; } };
  }

  var api = { create: createController, choose: choose, mapEntry: mapEntry, normalizeCodes: normalizeCodes, lowestLive: lowestLive };
  if (window.GibbonStoreDiscount) return;
  window.GibbonStoreDiscount = api;

  var started = false;
  function init() {
    if (started) return;
    started = true;
    var source = document.getElementById('gibbon-store-discount-config');
    if (!source) return;
    try {
      api.controller = createController(JSON.parse(source.textContent));
      api.stop = api.controller.start();
    } catch (error) {
      if (window.console && console.debug) console.debug('[Gibbon discounts] Keeping Liquid prices.', error);
    }
  }
  // Run after the page's own DOMContentLoaded handlers (setBoard, refreshPrices, the floating bar).
  if (document.readyState === 'complete') init();
  else {
    document.addEventListener('DOMContentLoaded', init, { once: true });
    window.addEventListener('load', init, { once: true });
  }
})();
